import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { sprintf } from 'sprintf-js';

import { apiFetchUserByEmail } from 'api/apiFetchUserByEmail';
import type { ListingApplicationPayload } from 'api/ats';
import { validateApplication } from 'api/ats';
import type { ApiListingApplicationListingType } from 'api/types/ApiListingApplicationListingType';
import type { ApiRequestedAttachment } from 'api/types/ApiRequestedAttachment';
import { Box } from 'components/Box/Box';
import type { FormUsageContext } from 'components/Form/Form';
import { Form } from 'components/Form/Form';
import type { FormSubmitFunction } from 'components/Form/FormContext';
import { GeneralError } from 'components/Form/GeneralError';
import { SubmitButton } from 'components/Form/SubmitButton/SubmitButton';
import { InfoBox } from 'components/InfoBox/InfoBox';
import { BodySmall } from 'components/Text/BodySmall';
import { Text } from 'components/Text/Text';
import { useTemporaryStorage } from 'hooks/useTemporaryStorage';
import { ListingApplicationFields } from 'modules/listing/components/ApplicationFields/ListingApplicationFields';
import { useJobAppliedConfetti } from 'modules/listing/components/JobAppliedConfetti/useJobAppliedConfetti';
import { userDashboardCreateListingApplication } from 'modules/userDashboard/zustand-stores/userDashboardCurrentListingApplicationsStore';
import { gutterWidth } from 'theme/theme';
import { trackEvent } from 'utils/analytics/track/trackEvent';
import { LISTING_TYPE_HUMAN_NAME_MAP } from 'utils/constants/general/listingTypeHumanNameMap';
import type { OrgType } from 'utils/constants/general/orgTypes';
import { isDefined } from 'utils/functional/type';
import { hasResumeDb } from 'utils/resumeDatabase';
import { showModal } from 'zustand-stores/modalStore';
import { useEnvironment, useUser } from 'zustand-stores/userEnvironmentStore';

import { ListingViewApplicationFormConsentField } from './ListingViewApplicationFormConsentField';

type Props = {
  analyticsTitle: string;
  usageContext: FormUsageContext;
  listingId: string;
  listingType: ApiListingApplicationListingType;
  orgId?: string;
  orgType?: OrgType;
  requestedAttachments: ApiRequestedAttachment[];
  requestedAttachmentsHash: string;
  isPreview?: boolean;
};

export function ListingViewApplicationForm({
  analyticsTitle,
  usageContext,
  isPreview,
  listingId,
  listingType,
  orgId,
  orgType,
  requestedAttachments,
  requestedAttachmentsHash,
}: Props) {
  const navigate = useNavigate();
  const [temporaryStorageSubmitted, setTemporaryStorageSubmitted] =
    useState(false);
  const { user } = useUser();
  const { environment } = useEnvironment();
  const [searchParams] = useSearchParams();

  const showOnboarding =
    !isDefined(user?.isRecruitable) && hasResumeDb(user, environment);

  const [saveTemporaryStorage, temporaryStorage] = useTemporaryStorage(
    user?.emailVerified && typeof window !== 'undefined'
      ? searchParams.get('tmpId')
      : '',
  );
  const formRef = useRef<HTMLFormElement>(null);

  const initialValues = useMemo(
    () =>
      'initialValues' in temporaryStorage
        ? temporaryStorage.initialValues
        : { requestedAttachmentsHash },
    [requestedAttachmentsHash, temporaryStorage],
  );

  useEffect(() => {
    if (
      initialValues &&
      // requestedAttachmentsHash is always there
      Object.keys(initialValues).length > 1 &&
      !temporaryStorageSubmitted &&
      formRef.current
    ) {
      setTemporaryStorageSubmitted(true);
      formRef.current.dispatchEvent(new Event('submit', { bubbles: true }));
    }
  }, [temporaryStorage, temporaryStorageSubmitted, initialValues]);

  const Confetti = useJobAppliedConfetti();
  const onSubmit: FormSubmitFunction<ListingApplicationPayload> = async ({
    handleError,
    values,
    updateSubmitting,
  }) => {
    try {
      if (user) {
        await userDashboardCreateListingApplication({ ...values });

        trackEvent('Success: Applied to Listing', {
          listing_id: listingId,
          listing_type: listingType,
          org_id: orgId || null,
          org_type: orgType || null,
          is_new_resume: user.resumeId !== values.resumeS3FileId,
        });

        const targetPath = showOnboarding
          ? '/dashboard/employment-profile/get-discovered?applied=true'
          : '/dashboard/application-history';

        if (showOnboarding) {
          navigate(targetPath);
          setTimeout(() => Confetti.showSimple(targetPath), 1000);
          return;
        }

        setTimeout(() => Confetti.showWithRedirect(targetPath), 1000);
        return;
      }

      await validateApplication(values);

      const [id, userExists] = await Promise.all([
        saveTemporaryStorage([
          { type: 'LISTING_APPLICATION_VALUES', data: values },
        ]),
        values.email && apiFetchUserByEmail(values.email),
      ]);

      updateSubmitting(false);
      const redirectTo = `${window.location.pathname}${
        window.location.search
      }${window.location.search ? '&' : '?'}tmpId=${id || ''}`;

      if (userExists) {
        return showModal('LOGIN', { redirectTo, email: values.email });
      }

      return showModal('SIGNUP', {
        title: getText('Just One More Step!'),
        description: (
          <Text mb={4}>
            {sprintf(
              getText(
                'Create an account on Idealist in order to finish applying to this %(listingType)s.',
              ),
              { listingType: LISTING_TYPE_HUMAN_NAME_MAP[listingType] },
            )}
          </Text>
        ),
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        redirectTo,
      });
    } catch (error) {
      handleError(error as Error);
    }
  };

  const initialUser = user
    ? { firstName: user.firstName, lastName: user.lastName, email: user.email }
    : {};

  const allFieldsRequired =
    requestedAttachments.filter((ra) => !ra.isRequired).length === 0;

  return (
    <Form
      analyticsTitle={analyticsTitle}
      usageContext={usageContext}
      data-qa-id="listing-application-form"
      initialValues={{
        ...initialUser,
        id: listingId,
        resumeS3FileId:
          listingType !== 'VOLOP' ? user?.resumeId || '' : undefined,
        consentAts: environment?.isEU ? false : undefined,
        requestedAttachments: Array(requestedAttachments.length).fill(null),
        type: listingType,
        replaceCurrentResume: false,
        ...initialValues,
      }}
      formRef={formRef}
      onSubmit={onSubmit}
    >
      <>
        {allFieldsRequired ? (
          <Box mt={-12} mb={3}>
            <InfoBox $type="warning" $small>
              <BodySmall>{getText('All fields are required')}</BodySmall>
            </InfoBox>
          </Box>
        ) : null}

        <GeneralError />

        <Box mt={24}>
          <ListingApplicationFields
            listingType={listingType}
            allFieldsRequired={allFieldsRequired}
            isPreview={Boolean(isPreview)}
            requestedAttachments={requestedAttachments}
          />
        </Box>

        <ListingViewApplicationFormConsentField
          isEU={Boolean(environment?.isEU)}
          disabled={isPreview}
        />

        <Box mt={gutterWidth} display="flex" justifyContent="flex-end">
          <SubmitButton size="large" disabled={isPreview}>
            {listingType === 'EVENT'
              ? getText('Join')
              : getText('Submit Application')}
          </SubmitButton>
        </Box>
      </>
    </Form>
  );
}
